import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { documentsApi } from '../../api/documents'
import { workbenchApi } from '../../api/workbench'
import { RefusedError } from '../../api/client'
import { useJobLive } from '../../hooks/useJobLive'
import { SESSIONS_KEY } from '../../components/CraftingSessionsNav'
import { sessionKey, work } from '../../lib/working'
import { answerSeen } from '../../lib/sessions'
import { useSpoke } from '../../hooks/useSpoke'
import type { ChatReply, MailThread, Opportunity, PersonNode, Workbench as WorkbenchState } from '../../types/workbench'
import { ThreadPreview } from './ThreadPreview'
import { Challenger } from './Challenger'
import { Chat, type PendingAttachment } from './Chat'
import { Draft } from './Draft'
import { SalesTeam, readSettings } from './SalesTeam'
import { Opportunities } from './Opportunities'
import { OpportunityPanel, type NewOpportunityPrefill } from './OpportunityPanel'
import { OPPORTUNITY_PARAM, effectOpportunity, matchPeople, withOpportunity } from './opportunityFocus'
import { Picker } from './Picker'
import { FindingsPanel, MailHistoryPanel, Refusal } from './WorkbenchParts'
import './workbench.css'

// THE WORKBENCH — one account, one person, one OPPORTUNITY, one conversation, one draft. The diagram on top is the
// account around the person; the opportunity panel on the left says what the message serves (chosen or made before
// a draft is crafted, REQ-121); the chat beneath it moves the stage; the draft on the right follows all three. Every
// fact on screen says where it came from. The only way out is the Outbox: the rep approves and sends there, never here.

export { Picker }

const why = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e))

export function Workbench() {
  const { companyId } = useParams()
  if (!companyId) return <Picker />
  return <AccountWorkbench companyId={companyId} />
}

function AccountWorkbench({ companyId }: { companyId: string }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const personId = params.get('person')
  // the opportunity the message serves rides on the route, like the person: a link can open the workbench on it
  const opportunityId = params.get(OPPORTUNITY_PARAM)
  // a derived opening being worked (opp:…) — a proposal, not a stored opportunity; it lights its card and rides on the turns
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [prefill, setPrefill] = useState<NewOpportunityPrefill | null>(null)
  const [researchJobId, setResearchJobId] = useState<string | null>(null)
  const [handedTo, setHandedTo] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState<string | null>(null)
  const [seed, setSeed] = useState<{ text: string; nonce: number } | null>(null)
  // a past communication opened for its preview (REQ-105)
  const [preview, setPreview] = useState<MailThread | null>(null)
  // files dropped into the conversation, held in the form until Send (2026-10-07)
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [clearOn, setClearOn] = useState(0)
  const [sentAt, setSentAt] = useState<number | null>(null)

  const key = ['workbench', spoke, companyId, personId, opportunityId]
  const q = useQuery({
    queryKey: key, queryFn: () => workbenchApi.get(spoke, companyId, personId ?? undefined, opportunityId ?? undefined), staleTime: 10_000,
    // picking a person or an opportunity re-keys the read; the page keeps standing on the same account while it lands
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === companyId ? prev : undefined),
  })
  const mail = useQuery({ queryKey: ['workbench-mail', spoke, companyId, personId], queryFn: () => workbenchApi.mail(spoke, companyId, personId!), enabled: !!personId, staleTime: 60_000 })
  const draftKey = ['workbench-draft', spoke, companyId, personId, opportunityId]
  const draft = useQuery({ queryKey: draftKey, queryFn: () => workbenchApi.draft(spoke, companyId, personId!, opportunityId ?? undefined), enabled: !!personId, retry: false })

  const select = useCallback((id: string) => { setHandedTo(null); setParams((p) => { const n = new URLSearchParams(p); n.set('person', id); return n }, { replace: true }) }, [setParams])
  // the draft is per person per opportunity: a different opportunity is a different draft, so what was handed over no longer applies
  const setOpportunity = useCallback((id: string | null) => { setHandedTo(null); setParams((p) => withOpportunity(p, id), { replace: true }) }, [setParams])

  // a turn answers with the whole state; the effects say what else to refresh
  const applyReply = (r: ChatReply) => {
    qc.setQueryData<WorkbenchState>(key, r.workbench)
    const fx = r.turn.effects
    if (fx?.selected && fx.selected !== personId) select(fx.selected)
    if (fx?.opportunity) {
      const hit = effectOpportunity(fx.opportunity, r.workbench.pursuits)
      if ('engagementId' in hit) { if (hit.engagementId !== opportunityId) setOpportunity(hit.engagementId) }
      else setOpeningId(hit.openingId)
    }
    if (fx?.research) setResearchJobId(fx.research)
    if (fx?.draftRevised || fx?.voiceCorrectionId) {
      void qc.invalidateQueries({ queryKey: ['workbench-draft', spoke, companyId] })
      void qc.invalidateQueries({ queryKey: ['workbench-challenge', spoke, companyId] })   // the review says whether it read this text
    }
    if (fx?.handedToOutbox) { setHandedTo(fx.handedToOutbox); void qc.invalidateQueries({ queryKey: ['outbox'] }) }
  }
  // the session this page is: the menu's spinner, the toast and the ledger all key on it
  const skey = sessionKey(companyId, personId, opportunityId)
  const who = q.data?.nodes.find((n) => n.kind === 'person' && n.personId === personId)?.label ?? (q.data?.account.kind === 'account' ? q.data.account.label : null) ?? 'this account'
  const here = `${window.location.pathname}${window.location.search}`
  // the ledger is told the session is open: what was waiting is seen, and the menu's dot goes — on arrival, and when the
  // tab comes back to the front, never from a page the rep has left
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const seen = useCallback(() => {
    if (!answerSeen(mounted.current, document.visibilityState)) return
    void workbenchApi.opened(spoke, companyId, personId, opportunityId).then(() => qc.invalidateQueries({ queryKey: [SESSIONS_KEY] })).catch(() => {})
  }, [spoke, companyId, personId, opportunityId, qc])
  useEffect(() => {
    seen()
    const onVisible = () => { if (document.visibilityState === 'visible') seen() }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [seen])
  const chat = useMutation({
    mutationFn: ({ text, oppId }: { text: string; oppId?: string }) => { setSentAt(Date.now()); work.start(skey, who); return workbenchApi.chat(spoke, companyId, text, personId ?? undefined, oppId ?? openingId ?? undefined, opportunityId ?? undefined) },
    // read by the cache off the page too: the spinner, the toast, the menu (main.tsx)
    meta: { working: skey, label: who, done: `Scout answered on ${who}`, href: here },
    // the answer is seen only if the rep is still here to see it: this handler runs even after the page is gone (the hook's
    // callbacks outlive the component), and a reply that lands while the rep is on another screen or the tab is in the
    // background is exactly what the hour's reminder is for (2026-10-14)
    onSuccess: (r) => { setUnavailable(null); setClearOn(Date.now()); applyReply(r); seen() },
    onError: (e) => { if (e instanceof RefusedError && (e.refusal.reason === 'ModelUnavailable' || e.refusal.reason === 'NotConfigured')) setUnavailable(e.refusal.message) },
    onSettled: () => setSentAt(null),
  })
  // A DOCUMENT DROPPED INTO THE CONVERSATION (REQ-119, and 2026-10-07): stored against the account, the person in focus and
  // the opportunity in focus (whichever are set) and read in the background while the rep keeps typing; it is named to Scout
  // only when the rep sends, with the words it was meant to go with. Don's file went as a turn of its own the second it
  // landed, before his paragraph was written; the page refetch it caused then showed no history at all.
  const attach = (file: File) => {
    const id = `${Date.now()}-${file.name}`
    setAttachments((list) => [...list, { id, name: file.name, status: 'reading' }])
    documentsApi.upload(spoke, file, { companyId, engagementId: opportunityId ?? undefined, personId: personId ?? undefined, origin: 'chat' })
      .then((r) => {
        setAttachments((list) => list.map((a) => (a.id === id ? { ...a, name: r.document.document.fileName || file.name, status: 'ready' } : a)))
        void qc.invalidateQueries({ queryKey: ['documents', spoke] })
      })
      .catch((e: unknown) => setAttachments((list) => list.map((a) => (a.id === id ? { ...a, status: 'failed', error: why(e) } : a))))
  }
  // the message: the words, and the files that go with them; the files are read already, so the turn can use them at once
  const sendMessage = (text: string) => {
    const files = attachments.filter((a) => a.status === 'ready').map((a) => a.name)
    setAttachments((list) => list.filter((a) => a.status === 'failed'))
    const named = files.length === 0 ? '' : files.length === 1 ? `Attached "${files[0]}" — use what it says.` : `Attached ${files.map((f) => `"${f}"`).join(', ')} — use what they say.`
    // the Wordsmith's standing instruction for this sitting rides on every turn (owner, 2026-10-12)
    const standing = readSettings('wordsmith').instruction
    const said = text && named ? `${text}\n\n${named}` : text || named
    chat.mutate({ text: standing ? `${said}\n\n(Standing instruction for this sitting: ${standing})` : said })
  }
  // a version put back (REQ-104): the draft answers as it now stands; the turns and the stage follow
  const restore = useMutation({
    mutationFn: (seq: number) => workbenchApi.restoreVersion(spoke, companyId, personId!, seq, opportunityId ?? undefined),
    onSuccess: (d) => { qc.setQueryData(draftKey, d); void qc.invalidateQueries({ queryKey: key }) },
  })
  // the thread it replies in (owner, 2026-10-12): a subject to reply in, or null for a fresh thread; the draft comes back re-threaded
  const setThread = useMutation({
    mutationFn: (subject: string | null) => workbenchApi.setThread(spoke, companyId, personId!, subject, opportunityId ?? undefined),
    onSuccess: (d) => { qc.setQueryData(draftKey, d); void qc.invalidateQueries({ queryKey: key }) },
  })
  const toOutbox = useMutation({
    mutationFn: () => workbenchApi.toOutbox(spoke, companyId, personId!, opportunityId ?? undefined),
    onSuccess: (v) => { setHandedTo(v.row.outboundId); void qc.invalidateQueries({ queryKey: ['outbox'] }); void qc.invalidateQueries({ queryKey: key }) },
  })

  // research the chat started: watch it, and refresh the state when it lands
  const { state: live } = useJobLive(spoke, researchJobId ?? undefined)
  const landed = live ? live.job.status === 'Completed' || live.job.status === 'Failed' || live.job.status === 'Cancelled' : false
  useEffect(() => { if (landed) { setResearchJobId(null); void qc.invalidateQueries({ queryKey: ['workbench', spoke, companyId] }) } }, [landed, qc, spoke, companyId])

  const wb = q.data
  const nameOf = useMemo(() => { const m = new Map((wb?.nodes ?? []).filter((n) => n.kind === 'person').map((n) => [n.kind === 'person' ? n.personId : n.id, n.label])); return (id: string) => m.get(id) ?? id }, [wb])
  const person = wb?.nodes.find((n) => n.kind === 'person' && n.personId === personId)
  const workOpportunity = (o: Opportunity) => { setOpeningId(o.id); chat.mutate({ text: `Work the ${o.label} opportunity`, oppId: o.id }) }
  // an opening made an opportunity: the form opens filled from the reading, its names matched to the stage's people
  const makeOpportunity = (o: Opportunity) => setPrefill({ subject: o.label, summary: o.situation, serviceLine: o.serviceLine, openingId: o.id, personIds: matchPeople(o.people, wb?.nodes ?? []), nonce: Date.now() })
  const busy = chat.isPending || toOutbox.isPending || restore.isPending || setThread.isPending
  const failed = [chat, toOutbox, restore].find((m) => m.isError && !(m === chat && unavailable))?.error
  const people = (wb?.nodes ?? []).filter((n): n is PersonNode => n.kind === 'person' && !n.background)

  if (q.isPending) return <p className="muted centered">Loading…</p>
  if (q.isError || !wb) {
    return (
      <section className="page workbench">
        <p className="kicker"><Link to="/workbench">Crafting</Link></p>
        <h1>Not on file</h1>
        <Refusal title="No such account" message={why(q.error)}><p><Link to="/workbench">Back to who to work next</Link></p></Refusal>
      </section>
    )
  }
  const account = wb.account.kind === 'account' ? wb.account : null

  return (
    <section className="page workbench">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/workbench">Crafting</Link> · {account?.label ?? 'account'}{account?.segment ? ` · ${account.segment}` : ''}</p>
          <h1>{person && person.kind === 'person' ? person.label : account?.label ?? 'The account'}</h1>
          <p className="lede">
            {person && person.kind === 'person' ? <>{person.title ? `${person.title} at ${account?.label ?? 'the account'}. ` : ''}The email in progress is on the right; tell the Wordsmith on its left what to change and the draft follows.</> : 'Pick a person at this account to start a draft, or say who to write to.'}
            {wb.anchoring && !wb.anchoring.ok && <span className="warn"> {wb.anchoring.why}</span>}
          </p>
        </div>
        <div className="actions">
          <Link className="button" to={`/accounts/${encodeURIComponent(companyId)}`}>Account plan</Link>
          <Link className="button" to="/outbox">Outbox</Link>
        </div>
      </header>

      {failed && <p className="error small" role="alert">{why(failed)}</p>}
      {researchJobId && (
        <p className="small researching" role="status">
          Researching: what is on file first, then the public web{live ? ` · ${live.job.steps.find((s) => s.status === 'Running')?.label ?? live.job.status.toLowerCase()}` : ''} · it is said in the conversation when it lands · <Link to={`/jobs/${encodeURIComponent(researchJobId)}`}>open the console</Link>
        </p>
      )}
      {live && live.job.status === 'Failed' && <Refusal title="The research could not be finished." message={live.job.error ?? 'The job failed.'} />}

      <div className="wb-grid">
        {/* THE CRAFTING BENCH (owner, 2026-10-12; sides swapped 2026-10-13): the Wordsmith on the left, the email in progress on the right; one row */}
        <div className="wb-bench">
          {/* THE WORDSMITH: the one agent that writes the email, at the draft's side */}
          <div className="wb-wordsmith card spotlight agent-panel agent-wordsmith">
            <Chat turns={wb.turns ?? []} onSend={sendMessage} busy={chat.isPending} modelConfigured={wb.modelConfigured ?? true} modelId={wb.modelId} unavailable={unavailable} nameOf={nameOf} seed={seed}
              onAttach={attach} attachments={attachments} onRemoveAttachment={(id) => setAttachments((list) => list.filter((a) => a.id !== id))} clearOn={clearOn} startedAt={sentAt} />
          </div>

          <div className="wb-top card">
            <h2 className="panel-title">The draft{person && person.kind === 'person' ? <span className="panel-to"> · to {person.label}</span> : ''}</h2>
            {wb.focus
              ? <p className="muted small draft-serves">Serves: <Link to={`/opportunities/${encodeURIComponent(wb.focus.engagementId)}`}>{wb.focus.subject}</Link></p>
              : <p className="muted small draft-serves">No opportunity chosen — the draft serves whatever the ranking guessed.</p>}
            {!personId && <p className="muted small">Pick a person at this account below, or say who to write to.</p>}
            {personId && draft.isPending && <p className="muted small">Reading the file and writing…</p>}
            {personId && draft.isError && <p className="muted small">{why(draft.error)}</p>}
            {personId && (
              <Draft draft={draft.data ?? wb.draft ?? null} busy={busy} handedTo={handedTo}
                to={person && person.kind === 'person' ? { name: person.label, address: mail.data?.coverage.addresses[0] ?? null } : undefined}
                onToOutbox={() => toOutbox.mutate()} onUseWording={(t) => chat.mutate({ text: `Use this wording: ${t}` })} onRestore={(seq) => restore.mutate(seq)}
                onTone={(instruction) => chat.mutate({ text: instruction })}
                threads={mail.data?.threads ?? []} onThread={(subject) => setThread.mutate(subject)} />
            )}
            {personId && (draft.data ?? wb.draft) && (
              <Challenger spoke={spoke} companyId={companyId} personId={personId} opportunityId={opportunityId} busy={busy}
                onApply={(instruction) => setSeed({ text: instruction, nonce: Date.now() })} />
            )}
          </div>
        </div>

        {/* THE REST OF YOUR SALES TEAM (owner, 2026-10-12): the Researcher, the Comms agent and the Strategy agent under the bench, each in its own frame; history kept per opportunity */}
        <section className="wb-team card" aria-label="The rest of your Sales Team">
          <div className="sales-team-head">
            <p className="kicker">The rest of your Sales Team</p>
            <span className="muted small">They find, remember and advise; anything they find goes into the email with one click. Only the Wordsmith writes it.</span>
          </div>
          <SalesTeam spoke={spoke} companyId={companyId} personId={personId} opportunityId={openingId} engagementId={wb.focus?.engagementId ?? null} busy={busy} draftText={(draft.data ?? wb.draft)?.text ?? null} personName={person && person.kind === 'person' ? person.label : null}
            onApply={(instruction) => setSeed({ text: instruction, nonce: Date.now() })} sessionKey={skey} who={who} here={here} onAnswered={seen} />
        </section>

        <aside className="wb-side card">
          <p className="kicker">Openings here</p>
          <Opportunities opportunities={wb.opportunities} selectedId={openingId} onSelect={workOpportunity} onMake={makeOpportunity} busy={busy} />
        </aside>

        {/* KEY INFORMATION: who, the opportunity; and the FEEDS: every message with them, the documents */}
        <div className="wb-info card">
          <h2 className="panel-title">{person && person.kind === 'person' ? person.label : 'Who'}</h2>
          {person && person.kind === 'person' && (
            <dl className="facts wb-person">
              {person.title && <><dt>Title</dt><dd>{person.title}</dd></>}
              {account && <><dt>Account</dt><dd><Link to={`/accounts/${encodeURIComponent(companyId)}`}>{account.label}</Link>{account.segment ? ` · ${account.segment}` : ''}</dd></>}
              <dt>Relationship</dt><dd>{Math.round((person.strength ?? 0) * 100)}%{person.currency && person.currency.state !== 'current' ? ' · may have moved' : ''}</dd>
              {person.howWeKnow && person.howWeKnow.length > 0 && <><dt>How we know them</dt><dd>{person.howWeKnow[0].text}</dd></>}
              <dt>Card</dt><dd><Link to={`/contacts/${encodeURIComponent(person.personId)}`}>Everything on file about them</Link></dd>
            </dl>
          )}
          {people.length > 0 && (
            <>
              <p className="kicker">People at this account</p>
              <ul className="plain wb-people">
                {people.slice(0, 12).map((p) => (
                  <li key={p.personId}>
                    <button type="button" className={`chip-filter ${p.personId === personId ? 'on' : ''}`} onClick={() => select(p.personId)} title={p.title ?? ''}>{p.label}</button>
                  </li>
                ))}
              </ul>
            </>
          )}
          <OpportunityPanel spoke={spoke} companyId={companyId} wb={wb} focusedId={opportunityId} onPick={setOpportunity} prefill={prefill} busy={busy} />
          {/* WHAT THE RESEARCH FOUND (TFY-32): the findings had no home once the stage came off; here they are, each with where it came from */}
          <FindingsPanel nodes={wb.nodes} personId={personId} />
          {personId && (
            <>
              <h3 className="panel-subtitle">Every message with them</h3>
              {mail.isPending && <p className="muted small">Searching…</p>}
              {mail.isError && <p className="muted small">{why(mail.error)}</p>}
              {mail.data && <MailHistoryPanel mail={mail.data} onPreview={setPreview} />}
              {preview && personId && <ThreadPreview companyId={companyId} personId={personId} thread={preview} onClose={() => setPreview(null)} />}
            </>
          )}
        </div>
      </div>
    </section>
  )
}
